import {
  NotificationOffsetUnit,
  NotificationOffsetUnitValue,
  NotificationTemplate
} from './notification-template.model';

type NotificationTemplateApi = Omit<NotificationTemplate, 'offsetUnit'> & {
  offsetUnit?: NotificationOffsetUnitValue | string;
};

export const mapOffsetUnit = (
  offsetUnit?: NotificationOffsetUnitValue | string
): NotificationOffsetUnitValue | undefined =>
  typeof offsetUnit === 'string'
    ? NotificationOffsetUnit[offsetUnit as keyof typeof NotificationOffsetUnit]
    : offsetUnit;

export const mapNotificationTemplatesFromApi = (
  templates?: NotificationTemplateApi[] | null
): NotificationTemplate[] =>
  (templates ?? []).map((template) => ({
    ...template,
    offsetUnit: mapOffsetUnit(template.offsetUnit)
  }));

export const getNotificationTemplateKey = (template: NotificationTemplateApi): string =>
  `${template.offsetValue ?? ''}-${mapOffsetUnit(template.offsetUnit) ?? ''}`;

export const formatNotificationTemplateLabel = (template: NotificationTemplateApi): string => {
  const value = template.offsetValue ?? 0;
  const unit = mapOffsetUnit(template.offsetUnit) ?? NotificationOffsetUnit.Undefined;

  switch (unit) {
    case NotificationOffsetUnit.Minutes:
      return value === 1
        ? $localize`:@@TASKS_NOTIFICATION_MINUTE_BEFORE:1 minute before`
        : $localize`:@@TASKS_NOTIFICATION_MINUTES_BEFORE:${value}:PH: minutes before`;
    case NotificationOffsetUnit.Hours:
      return value === 1
        ? $localize`:@@TASKS_NOTIFICATION_HOUR_BEFORE:1 hour before`
        : $localize`:@@TASKS_NOTIFICATION_HOURS_BEFORE:${value}:PH: hours before`;
    case NotificationOffsetUnit.Days:
      return value === 1
        ? $localize`:@@TASKS_NOTIFICATION_DAY_BEFORE:1 day before`
        : $localize`:@@TASKS_NOTIFICATION_DAYS_BEFORE:${value}:PH: days before`;
    case NotificationOffsetUnit.Weeks:
      return value === 1
        ? $localize`:@@TASKS_NOTIFICATION_WEEK_BEFORE:1 week before`
        : $localize`:@@TASKS_NOTIFICATION_WEEKS_BEFORE:${value}:PH: weeks before`;
    case NotificationOffsetUnit.Months:
      return value === 1
        ? $localize`:@@TASKS_NOTIFICATION_MONTH_BEFORE:1 month before`
        : $localize`:@@TASKS_NOTIFICATION_MONTHS_BEFORE:${value}:PH: months before`;
    default:
      return `${value}`;
  }
};

export const isDuplicateNotificationTemplate = (
  templates: NotificationTemplate[],
  candidate: Pick<NotificationTemplate, 'offsetValue' | 'offsetUnit'>
): boolean => templates.some((template) => getNotificationTemplateKey(template) === getNotificationTemplateKey(candidate));
